import { UserCard } from '@/entities/user'
import { IUser } from '@/entities/user/model/types'
import { TTokensFundraise, viewContractData } from '@/features/fundraise-block/ui/use-fundraise'
import { contractRead, parseAmount } from '@/shared/lib'
import useNotifier from '@/shared/lib/use-notifier'
import { LangStore } from '@/shared/model/store'
import { ContractsStore } from '@/shared/model/store/contracts-store'
import { Button, Dialog, DialogTitle, DialogTrigger, DialogContent } from '@/shared/ui'
import { AmountBlock } from '@/shared/ui/amount-block/amount-block'
import AmountInput from '@/shared/ui/amount-input/ui/amount-input'
import LoadingIcon from '@/shared/ui/loading-icon'
import { Banknote, CoinsIcon, Edit2Icon, X } from 'lucide-react'
import { useEffect, useState } from 'react'

type Props = {
	user: IUser
	npo_address: `0x${string}`
}

type LimitsType = TTokensFundraise & {
	limit: number
}

const LimitsModal = ({ user, npo_address }: Props) => {
	const [isOpen, setIsOpen] = useState<boolean>(false)
	const { langPack } = LangStore()
	const [isLoadingData, setIsLoadingData] = useState<boolean>(true)
	const [limits, setLimits] = useState<LimitsType[]>([])
	const [editMode, setEditMode] = useState<LimitsType>({} as LimitsType)
	const { writeContract } = useNotifier({
		onSuccess() {
			setEditMode({} as LimitsType)
			getData()
		}
	})
	const { contracts } = ContractsStore()

	const getData = async () => {
		const list: LimitsType[] = []
		const supportedTokens: `0x${string}`[] = await contractRead('SavvaNPO', 'getSupportedTokens', [], npo_address)
		for (let i = 0; i < supportedTokens.length; i++) {
			const res = await contractRead(
				'SavvaNPO',
				'getMemberWeeklyLimit',
				[user.address, supportedTokens[i]],
				npo_address
			)
			const cd = await viewContractData(supportedTokens[i])
			list.push({ ...cd, address: supportedTokens[i], limit: Number(res) })
		}
		setLimits(list)
		setIsLoadingData(false)
	}

	const setLimit = () => {
		writeContract({
			abi: contracts.SavvaNPO.abi,
			address: npo_address || '0x0000000000000000000000000000000000000000',
			functionName: 'updateWeeklyLimit',
			args: [user.address, editMode.address, parseAmount(editMode.limit, editMode.decimals)]
		})
	}

	const openHandler = state => {
		if (!state) {
			setLimits([])
			setIsLoadingData(true)
		}
		setIsOpen(state)
	}

	useEffect(() => {
		if (!isOpen) return
		getData()
	}, [isOpen])
	return (
		<Dialog onOpenChange={openHandler} open={isOpen}>
			<DialogTrigger>
				<Button size='sm' variant='secondary'>
					<CoinsIcon size={16} />
				</Button>
			</DialogTrigger>
			<DialogContent className='sm:max-w-[625px]'>
				<DialogTitle>Limits</DialogTitle>
				<div className='w-full rounded-lg p-4 shadow-md'>
					<UserCard user={user} />
				</div>
				{isLoadingData ? (
					<div className='mt-4 flex items-center justify-center'>
						<LoadingIcon onlyText />
					</div>
				) : (
					<div className='flex flex-col gap-4'>
						{limits.map(limit => (
							<div className='flex w-full items-center justify-between rounded-lg p-4 shadow-md'>
								<div className='flex items-center gap-2'>
									<span>{limit.symbol}</span>
								</div>
								<div className='flex gap-4'>
									{editMode.address === limit.address ? (
										<AmountInput
											value={editMode.limit}
											onChange={value => setEditMode({ ...editMode, limit: value })}
										/>
									) : (
										<AmountBlock amount={limit.limit} withoutIcon />
									)}
									{editMode.address === limit.address ? (
										<div className='flex items-center gap-2'>
											<Button onClick={setLimit} variant={'success'}>
												{langPack('action.save')}
											</Button>
											<Button variant='secondary' onClick={() => setEditMode({} as LimitsType)}>
												<X size={12} />
											</Button>
										</div>
									) : (
										<Button
											variant='secondary'
											onClick={() => setEditMode({ ...limit, limit: limit.limit / 10 ** limit.decimals })}
										>
											<Edit2Icon size={12} />
										</Button>
									)}
								</div>
							</div>
						))}
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}

export default LimitsModal
